import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  UploadFile,
  UploadInput,
  UploadOutput,
  UploadStatus,
  UploaderOptions,
} from 'ngx-uploader';
import { v4 as uuid } from 'uuid';
import { RRIFileType } from '../../interfaces/file-type.interface';
import { FileUploadContainer } from '../../models/file-upload-container.model';
import { RRIUploadSettings } from '../../interfaces/upload-settings.interface';
import { RRFileContainerModel } from '../../models/file-container-model.model';
import { RRIFileUploadError } from '../../interfaces/file-upload-error.interface';
import { RR_THUMBNAIL } from '../../../common/constants/rr_thumbnail';

@Component({
  selector: 'rr-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
})
export class FileUploadComponent<T extends RRFileContainerModel>
  implements OnChanges
{
  public uploadOptions: UploaderOptions;
  public files: FileUploadContainer<T>[];
  public uploadedFiles: FileUploadContainer<T>[];
  public uploadInput: EventEmitter<UploadInput>;
  public isUploading: boolean;
  public UploadStatus = UploadStatus;
  public fileTypeModel: RRIFileType;
  public RR_THUMBNAIL = RR_THUMBNAIL;

  public readonly SINGLE_FILE_UPLOAD_TEXT_SECOND_LINE = 'upload file';
  public readonly MULTI_FILE_UPLOAD_TEXT_SECOND_LINE = 'upload files';
  public fileUploadTextSecondLine = this.MULTI_FILE_UPLOAD_TEXT_SECOND_LINE;

  @Input() maxUploads: number;
  @Input() hideUploadButton: boolean;
  @Input() shouldStartUpload: boolean;
  @Input() fileContainerModel: T;
  @Input() fileTypes: RRIFileType[];
  @Input() uploadType: string;
  @Input() urlFormatter: (
    fileContainer: FileUploadContainer<RRFileContainerModel>,
    standardUrl: string,
  ) => string;
  @Input() uploadSettings: RRIUploadSettings;
  @Input() supportedFileExtensions: string[];
  @Input() disabled: boolean;
  @Input() isRequired: boolean;
  @Input() isFileValid: (file: UploadFile) => Promise<boolean>;
  @Output() uploadFinished: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() uploadStart: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() filesToUpload: EventEmitter<any> = new EventEmitter<any>();
  @Output()
  fileUploadError: EventEmitter<RRIFileUploadError> =
    new EventEmitter<RRIFileUploadError>();
  @Output() uploadFileFinished: EventEmitter<
    FileUploadContainer<RRFileContainerModel>[]
  > = new EventEmitter<FileUploadContainer<RRFileContainerModel>[]>();
  constructor() {
    this.uploadOptions = { concurrency: 1 };
    this.uploadOptions.allowedContentTypes;
    this.files = [];
    this.uploadedFiles = [];
    this.uploadInput = new EventEmitter<UploadInput>();
    this.isUploading = false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.maxUploads === 1) {
      this.fileUploadTextSecondLine = this.SINGLE_FILE_UPLOAD_TEXT_SECOND_LINE;
    }
    if (
      changes.fileTypes &&
      changes.fileTypes.isFirstChange() &&
      changes.fileTypes.currentValue !== undefined &&
      changes.fileTypes.currentValue.length > 0
    ) {
      this.fileTypeModel = this.fileTypes[0];
    }

    if (
      changes.shouldStartUpload &&
      changes.shouldStartUpload.previousValue === false &&
      changes.shouldStartUpload.currentValue === true
    ) {
      this.startUpload();
    }

    this.uploadOptions.allowedContentTypes = this.supportedFileExtensions;
  }

  public async onUploadOutput(output: UploadOutput): Promise<void> {
    if (
      output.type === 'addedToQueue' &&
      typeof output.file !== 'undefined' &&
      this.fileContainerModel !== undefined
    ) {
      if (typeof this.isFileValid === 'function') {
        if (!(await this.isFileValid(output.file))) {
          return;
        }
      }

      const container = new FileUploadContainer<T>();
      container.file = output.file;
      const imagePreview = await this.previewImage(output.file);

      if (imagePreview) {
        container.file.imagePreview = imagePreview.src;
      } else {
        container.file.imagePreview = '';
      }

      const newUUID = uuid();

      container.containerModel = new RRFileContainerModel() as T;
      container.containerModel.metadata = this.fileContainerModel.metadata;
      container.containerModel.configurationResultId =
        this.fileContainerModel.configurationResultId;
      container.containerModel.configurationId =
        this.fileContainerModel.configurationId;
      container.containerModel.typeId = this.fileContainerModel.typeId
        ? this.fileContainerModel.typeId
        : this.fileTypes && this.fileTypes.length
        ? `${this.fileTypes[0].id}`
        : '0';
      container.containerModel.name = output.file.name;
      container.containerModel.id = newUUID;

      if (this.maxUploads == 1) {
        this.files.pop();
        this.files = [container];
      } else {
        this.files.push(container);
      }
      this.filesToUpload.emit(this.files);
    } else if (output.type == 'done') {
      if (
        output.file.responseStatus === 200 ||
        output.file.responseStatus === 201 ||
        output.file.responseStatus === 204
      ) {
        this.checkUploadFinished(output.file.id);
      } else {
        this.uploadFinished.emit(false);
        this.checkUploadFinished(output.file.id);
        this.fileUploadError.emit({
          message: output.file.response,
          status: output.file.responseStatus,
        });
      }
    }
  }

  public removeFile(target: FileUploadContainer<T>): void {
    this.files = this.files.filter((item) => item != target);
    this.filesToUpload.emit(this.files);
  }

  public startUpload(): void {
    this.isUploading = true;
    this.uploadStart.emit(true);

    for (const container of this.files) {
      let dataContainer = container.containerModel.toDataFormat();
      if (this.uploadType === 'input') {
        dataContainer = container.containerModel.toInputFormat(
          container.containerModel,
        );
      }

      let url: string = this.uploadSettings.url;
      if (this.urlFormatter) {
        url = this.urlFormatter(container, url);
      }

      const event: UploadInput = {
        ...this.uploadSettings,
        file: container.file,
        data: dataContainer,
        url,
      };

      this.uploadInput.emit(event);
    }
  }

  public changeInputTypeModel(event, model: any = null): void {
    if (model != null) {
      model.typeId = event;
    } else {
      for (const container of this.files) {
        container.containerModel.typeId = event;
      }
    }
  }

  private checkUploadFinished(fileId): void {
    const containerIndex = this.files.indexOf(
      this.files.find((x) => x.file.id === fileId),
    );

    if (containerIndex > -1) {
      this.uploadedFiles.push(this.files[containerIndex]);

      if (this.uploadedFiles.length == this.files.length) {
        this.isUploading = false;
        this.uploadFinished.emit(true);
        this.uploadFileFinished.emit(this.uploadedFiles);
      }
    }
  }

  private previewImage(file: UploadFile): Promise<HTMLImageElement> {
    const imageTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
    if (imageTypes.includes(file.type)) {
      const fileReader = new FileReader();
      return new Promise((resolve) => {
        fileReader.readAsDataURL(file.nativeFile);
        fileReader.onload = (e: any) => {
          const image = new Image();
          image.src = e.target.result;
          resolve(image);
        };
      });
    } else {
      return Promise.resolve(null);
    }
  }
}
